import {walkthroughNarrationMatches} from './verifiedWalkthroughNarration.js';
import {WALKTHROUGH_STEPS} from './walkthrough.js';

export const WALKTHROUGH_WELCOME = [
  'Welcome to QLine The Game, for family game nights, rehearsals, and so much more.',
  'Play a murder mystery or other story with family and friends where you get to be part of the adventure.',
  'Actors and directors can also rehearse with the script out of their hands, so actors can look up, listen, react, and stay in the scene.',
  'QLine gives each player or actor private cues through their own phone, so the game or rehearsal can keep moving without everyone staring at a script.',
  'Would you like a quick walkthrough showing you how it works?',
];
export const WALKTHROUGH_FINISH = [
  'The Director runs the game or rehearsal. Each player or actor puts in an earpiece, joins the room, and follows their private cues.',
  'QLine handles the prompting in the background so you can look up, listen, react, and focus on the adventure or scene instead of staring at a script.',
];
export const WALKTHROUGH_AUDIO = Object.freeze([
  {id:'welcome', paragraphs:WALKTHROUGH_WELCOME},
  ...WALKTHROUGH_STEPS.map((step, index) => ({id:`step-${String(index + 1).padStart(2, '0')}`, paragraphs:step.paragraphs})),
  {id:'finish', paragraphs:WALKTHROUGH_FINISH},
].map(({id, paragraphs}) => Object.freeze({id, src:`/audio/walkthrough/v1/${id}.wav`, narration:paragraphs.join(' '), narrationAvailable:walkthroughNarrationMatches(id,paragraphs.join(' '))})));

// Owns only tour audio, never the room's playback engine. Each source has its
// own fenced callbacks so a late rejection cannot change the next page's state.
export function createWalkthroughNarrator(report, makeAudio = src => { const clip = new Audio(); clip.preload = 'none'; clip.src = src; return clip; }) {
  let audio = null, generation = 0, enabled = false, disposed = false;
  const status = value => { if (!disposed) report(value); };
  function stop() {
    generation++;
    if (audio) {
      audio.onended = audio.onerror = null;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audio = null;
    }
  }
  function play(replay = false) {
    enabled = true;
    if (!audio || disposed) return;
    if (replay || audio.ended) audio.currentTime = 0;
    const current = ++generation;
    audio.onerror = () => { if (current === generation) status('Narration unavailable. You can keep reading or retry.'); };
    audio.onended = () => { if (current === generation) status('Narration finished'); };
    if (audio.error) audio.load();
    status('Loading narration…');
    try {
      Promise.resolve(audio.play()).then(() => {
        if (current === generation) status('Narration playing');
      }, () => { if (current === generation) status('Narration unavailable. You can keep reading or retry.'); });
    } catch { status('Narration unavailable. You can keep reading or retry.'); }
  }
  return {
    select(page, consent = false) {
      stop();
      if (disposed) return;
      const recording = WALKTHROUGH_AUDIO[page + 1];
      if (!recording?.narrationAvailable) { status('Narration unavailable for this page. You can keep reading.'); return; }
      const current = generation;
      try {
        audio = makeAudio(recording.src);
        audio.preload = 'none';
        audio.onerror = () => { if (current === generation) status('Narration unavailable. You can keep reading or retry.'); };
        audio.onended = () => { if (current === generation) status('Narration finished'); };
        status('Narration ready');
        if (consent) enabled = true;
        if (enabled) play();
      } catch { status('Narration unavailable. You can keep reading or retry.'); }
    },
    play,
    pause() { enabled = false; generation++; audio?.pause(); status('Narration paused'); },
    dispose() { enabled = false; disposed = true; stop(); },
  };
}
